import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link } from 'react-router-dom';
import api from '../api/api';
import { useTheme } from '../hooks/useTheme';
import { formatQuarterLabel } from '../utils/utils';
import SurveyModal from '../components/SurveyModal';
import AddTaskModal from '../components/AddTaskModal';
import ProgressReportSkeleton from '../components/layout/ProgressReportSkeleton';
import { useSurvey } from '../hooks/useSurvey';
import { TrendingUp, Target, Circle, CheckCircle2, Calendar, Award, BarChart3, Plus, X, Trash2, ArrowRight } from 'lucide-react';

export default function ProgressReport() {
  const { isDarkMode } = useTheme();
  const { pendingSurvey, checkSurveys, dismissSurvey, completeSurvey } = useSurvey();
  const surveyChecked = useRef(false);
  const [tasks, setTasks] = useState([]);
  const [quarterRows, setQuarterRows] = useState([]);
  const [quarterModal, setQuarterModal] = useState(null);
  const [savingTaskId, setSavingTaskId] = useState(null);
  const [createTaskForQuarter, setCreateTaskForQuarter] = useState(null);
  const [removingQuarterTaskId, setRemovingQuarterTaskId] = useState(null);
  const [weeklyFocusProgress, setWeeklyFocusProgress] = useState([]);
  const [weeklyGoal, setWeeklyGoal] = useState(3);
  const [goalInput, setGoalInput] = useState('3');
  const [editingGoal, setEditingGoal] = useState(false);
  const [savingGoal, setSavingGoal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [quarterStatusFilter, setQuarterStatusFilter] = useState('all');
  const [updatingQuarterTaskId, setUpdatingQuarterTaskId] = useState(null);

  // Check for progress_report survey trigger on page load (once)
  useEffect(() => {
    if (!surveyChecked.current) {
      surveyChecked.current = true;
      checkSurveys('progress_report');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch tasks
  useEffect(() => {
    const fetchData = async () => {
      try {
        const tasksResponse = await api.get("/task/activetasks");
        const quartersResponse = await api.get('/task/quarterly-overview').catch(() => null);
        const weeklyProgressResponse = await api.get('/task/weekly-progress').catch(() => null);
        
        if (Array.isArray(tasksResponse.data)) {
          setTasks(tasksResponse.data);
        }
        if (Array.isArray(quartersResponse?.data?.quarters)) {
          setQuarterRows(quartersResponse.data.quarters);
        }
        if (Array.isArray(weeklyProgressResponse?.data?.weeklyFocusProgress)) {
          setWeeklyFocusProgress(weeklyProgressResponse.data.weeklyFocusProgress);
        }
        if (weeklyProgressResponse?.data?.weeklyGoal) {
          setWeeklyGoal(weeklyProgressResponse.data.weeklyGoal);
          setGoalInput(String(weeklyProgressResponse.data.weeklyGoal));
        }
      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSaveWeeklyGoal = async () => {
    const parsed = parseInt(goalInput, 10);
    if (!Number.isInteger(parsed) || parsed < 1) return;
    setSavingGoal(true);
    try {
      await api.patch('/task/weekly-goal', { weeklyGoal: parsed });
      setWeeklyGoal(parsed);
      const refreshed = await api.get('/task/weekly-progress').catch(() => null);
      if (Array.isArray(refreshed?.data?.weeklyFocusProgress)) {
        setWeeklyFocusProgress(refreshed.data.weeklyFocusProgress);
      }
      setEditingGoal(false);
    } catch (err) {
      console.error('Error updating weekly goal:', err);
    } finally {
      setSavingGoal(false);
    }
  };

  // Calculate progress metrics
  const progressMetrics = useMemo(() => {
    const now = new Date();
    const yearStart = new Date(now.getFullYear(), 0, 1);
    const yearEnd = new Date(now.getFullYear(), 11, 31, 23, 59, 59);
    
    // Tasks completed this year (assuming completed tasks have a status or date)
    const tasksThisYear = tasks.filter(task => {
      if (!task.EndDate && !task.CompletedDate) return false;
      const taskDate = task.CompletedDate ? new Date(task.CompletedDate) : new Date(task.EndDate);
      return taskDate >= yearStart && taskDate <= yearEnd;
    });
    
    // Total tasks
    const totalTasks = tasks.length;
    const completedTasks = tasks.filter((t) => String(t.Status || t.status || '').toLowerCase() === 'completed').length;
    const completionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
    
    // Tasks by focus area
    const tasksByFocusArea = tasks.reduce((acc, task) => {
      const area = task.FocusAreaName || 'Uncategorized';
      acc[area] = (acc[area] || 0) + 1;
      return acc;
    }, {});
    
    const topFocusArea = Object.entries(tasksByFocusArea)
      .sort(([, a], [, b]) => b - a)[0]?.[0] || 'None';
    
    const goals = weeklyFocusProgress.map((row, index) => ({
      id: index + 1,
      title: `${row.focusArea} weekly goal`,
      completed: Number(row.completed || 0) >= Number(row.weeklyGoal || 3),
      due: 'This week'
    }));
    
    const completedGoals = goals.filter(g => g.completed).length;
    const goalsProgress = goals.length > 0 ? Math.round((completedGoals / goals.length) * 100) : 0;
    
    // Meeting goals calculation (based on completion rate)
    const meetingGoals = completionRate >= 70 ? 'exceeding' : completionRate >= 50 ? 'on-track' : 'needs-improvement';
    
    return {
      tasksCompletedThisYear: tasksThisYear.length,
      totalTasks,
      completedTasks,
      completionRate,
      topFocusArea,
      goals,
      completedGoals,
      goalsProgress,
      meetingGoals
    };
  }, [tasks, weeklyFocusProgress]);

  const { tasksCompletedThisYear, totalTasks, completedTasks, completionRate, topFocusArea, goals, completedGoals, goalsProgress, meetingGoals } = progressMetrics;

  const today = new Date();

  const formatDueDateLabel = (rawDate) => {
    if (!rawDate) return 'N/A';
    const parsed = new Date(rawDate);
    if (Number.isNaN(parsed.getTime())) return String(rawDate);
    return parsed.toLocaleDateString();
  };

  const currentQuarter = useMemo(() => {
    if (!Array.isArray(quarterRows) || quarterRows.length === 0) return null;
    const flagged = quarterRows.find((q) => Number(q.IsCurrent) === 1);
    if (flagged) return flagged;

    return (
      quarterRows.find((q) => {
        const start = q.QuarterStartDate ? new Date(q.QuarterStartDate) : null;
        const end = q.QuarterEndDate ? new Date(q.QuarterEndDate) : null;
        return start && end && today >= start && today <= end;
      }) || null
    );
  }, [quarterRows, today]);

  const quarterCards = useMemo(() => {
    if (!Array.isArray(quarterRows) || quarterRows.length === 0) return [];

    return quarterRows.slice(0, 4).map((q) => ({
      quarterId: q.QuarterID,
      quarter: formatQuarterLabel(q),
      tasks: Array.isArray(q.tasks)
        ? q.tasks
            .map((t, idx) => ({
              id: t.TaskID || t.taskID || t.taskId || `${q.QuarterID}-${idx}`,
              quarterTaskId: t.QuarterTaskID,
              title: t.Title || t.title || t.TaskDesc || t.Description,
              dueDate: t.DueDate || t.dueDate || t.EndDate || t.endDate || t.due || null,
              status: t.Status || t.status || 'Not Started',
            }))
            .filter((t) => t.title)
        : [],
    }));
  }, [quarterRows]);

  const availableModalTasks = useMemo(() => {
    if (!quarterModal) return [];
    const selected = quarterRows.find((q) => String(q.QuarterID) === String(quarterModal.quarterId));
    const alreadyAssigned = new Set((selected?.tasks || []).map((t) => String(t.TaskID || t.taskID || t.taskId || t.id)));
    const qStart = selected?.QuarterStartDate ? new Date(selected.QuarterStartDate) : null;
    const qEnd = selected?.QuarterEndDate ? new Date(selected.QuarterEndDate) : null;

    return tasks
      .map((t) => ({
        id: t.TaskID || t.taskID || t.taskId || t.id,
        title: t.Title || t.title || t.Description || t.TaskDesc,
        dueDate: t.DueDate || t.dueDate || t.EndDate || t.endDate || null,
        status: t.Status || t.status,
      }))
      .filter((t) => t.id && t.title)
      .filter((t) => String(t.status).toLowerCase() !== 'completed')
      .filter((t) => !alreadyAssigned.has(String(t.id)))
      // Only show tasks whose due date actually falls within this quarter's date range
      // (e.g. Q1 = Jan-Mar), so you can't accidentally file a task under the wrong quarter.
      .filter((t) => {
        if (!qStart || !qEnd || !t.dueDate) return false;
        const due = new Date(t.dueDate);
        if (Number.isNaN(due.getTime())) return false;
        return due >= qStart && due <= qEnd;
      });
  }, [quarterModal, quarterRows, tasks]);

  const openQuarterModal = (card) => {
    setQuarterModal(card);
  };

  const closeQuarterModal = () => {
    setQuarterModal(null);
    setSavingTaskId(null);
  };

  const assignTaskToQuarter = async (task) => {
    if (!quarterModal?.quarterId) return;

    try {
      setSavingTaskId(String(task.id));
      await api.post('/task/quarterly-tasks', {
        quarterId: quarterModal.quarterId,
        taskId: task.id,
      });

      setQuarterRows((prev) =>
        prev.map((q) => {
          if (String(q.QuarterID) !== String(quarterModal.quarterId)) return q;
          const exists = Array.isArray(q.tasks)
            ? q.tasks.some((t) => String(t.TaskID || t.taskID || t.taskId || t.id) === String(task.id))
            : false;

          if (exists) return q;

          return {
            ...q,
            tasks: [
              ...(Array.isArray(q.tasks) ? q.tasks : []),
              { TaskID: task.id, Title: task.title },
            ],
          };
        })
      );
      closeQuarterModal();
    } catch (err) {
      window.alert(err?.response?.data?.error || 'Failed to assign task to quarter');
      setSavingTaskId(null);
    }
  };

  const refetchQuarters = async () => {
    const quartersResponse = await api.get('/task/quarterly-overview').catch(() => null);
    if (Array.isArray(quartersResponse?.data?.quarters)) {
      setQuarterRows(quartersResponse.data.quarters);
    }
  };

  const openCreateTaskForQuarter = (card) => {
    setQuarterModal(null);
    setCreateTaskForQuarter(card);
  };

  const closeCreateTaskForQuarter = () => {
    setCreateTaskForQuarter(null);
  };

  const handleQuarterTaskCreated = async (createdTask) => {
    const quarterId = createTaskForQuarter?.quarterId;
    const taskId = createdTask?.task_id ?? createdTask?.TaskID ?? createdTask?.taskId ?? createdTask?.id;
    if (quarterId && taskId) {
      try {
        await api.post('/task/quarterly-tasks', { quarterId, taskId });
      } catch (err) {
        window.alert(err?.response?.data?.error || 'Task was created but could not be added to the quarter');
      }
    }
    await refetchQuarters();
    setCreateTaskForQuarter(null);
  };

  const removeQuarterTask = async (task) => {
    if (!task.quarterTaskId) return;
    try {
      setRemovingQuarterTaskId(String(task.quarterTaskId));
      await api.delete(`/task/quarterly-tasks/${task.quarterTaskId}`);
      setQuarterRows((prev) =>
        prev.map((q) => ({
          ...q,
          tasks: Array.isArray(q.tasks)
            ? q.tasks.filter((t) => String(t.QuarterTaskID) !== String(task.quarterTaskId))
            : q.tasks,
        }))
      );
    } catch (err) {
      window.alert(err?.response?.data?.error || 'Failed to remove task from quarter');
    } finally {
      setRemovingQuarterTaskId(null);
    }
  };

  // All 4 quarters, with the current quarter moved to the front (top-left slot).
  const orderedQuarterCards = useMemo(() => {
    if (!quarterCards.length) return [];
    const currentId = currentQuarter?.QuarterID;
    if (currentId == null) return quarterCards;
    const current = quarterCards.find((c) => String(c.quarterId) === String(currentId));
    if (!current) return quarterCards;
    return [current, ...quarterCards.filter((c) => c !== current)];
  }, [quarterCards, currentQuarter]);

  const getFilteredQuarterTasks = (card) => {
    const cardTasks = card?.tasks || [];
    if (quarterStatusFilter === 'completed') {
      return cardTasks.filter((t) => t.status === 'Completed');
    }
    if (quarterStatusFilter === 'unfinished') {
      return cardTasks.filter((t) => t.status !== 'Completed');
    }
    return cardTasks;
  };

  const handleQuarterTaskStatusChange = async (task, nextStatus) => {
    if (!task.id) return;
    try {
      setUpdatingQuarterTaskId(String(task.id));
      await api.patch(`/task/status/${task.id}`, { status: nextStatus });
      setQuarterRows((prev) =>
        prev.map((q) => {
          if (!Array.isArray(q.tasks)) return q;
          return {
            ...q,
            tasks: q.tasks.map((t) => {
              const tId = t.TaskID || t.taskID || t.taskId;
              if (String(tId) !== String(task.id)) return t;
              return { ...t, Status: nextStatus };
            }),
          };
        })
      );
    } catch (err) {
      console.error('Error updating quarter task status:', err);
      window.alert(err?.response?.data?.error || 'Failed to update task status');
    } finally {
      setUpdatingQuarterTaskId(null);
    }
  };

  if (loading) {
    return <ProgressReportSkeleton isDarkMode={isDarkMode} />;
  }

  return (
    <>
    <div className={`min-h-screen ${isDarkMode ? 'bg-surface' : 'bg-surface'}`}>
      <div className="max-w-6xl mx-auto px-4 py-6 md:py-8">
        {/* Header */}
        <header className="mb-6 md:mb-10">
          <h1 className={`text-2xl md:text-3xl font-extrabold tracking-tight mb-2 ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Progress Report</h1>
          <p className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>
            Track your achievements, goals, and progress toward your career milestones.
          </p>
        </header>

        {/* Progress Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-8">
          {/* Tasks Completed This Year Card */}
          <div className={`rounded-2xl border shadow-sm p-4 md:p-6 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
            <div className="flex items-center justify-between mb-2 md:mb-4">
              <div className={`p-2 md:p-3 rounded-xl ${isDarkMode ? 'bg-primary-900/50 text-primary-400' : 'bg-primary-50 text-primary-600'}`}>
                <CheckCircle2 size={24} />
              </div>
              <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                isDarkMode ? 'text-primary-300 bg-primary-900/50' : 'text-primary-700 bg-primary-100'
              }`}>
                This Year
              </span>
            </div>
            <h3 className={`text-sm font-semibold mb-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Tasks Completed</h3>
            <div className="flex items-baseline gap-2">
              <span className={`text-2xl md:text-4xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>{tasksCompletedThisYear}</span>
              <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>tasks</span>
            </div>
            {tasksCompletedThisYear > 0 ? (
              <p className={`text-xs mt-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                Great progress!
              </p>
            ) : (
              <Link
                to="/actionitems"
                className={`mt-2 inline-flex items-center gap-1 text-xs font-semibold ${isDarkMode ? 'text-primary-400 hover:text-primary-300' : 'text-primary-600 hover:text-primary-700'}`}
              >
                Add a task
                <ArrowRight size={14} />
              </Link>
            )}
          </div>

          {/* Goals Card */}
          <div className={`rounded-2xl border shadow-sm p-4 md:p-6 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
            <div className="flex items-center justify-between mb-2 md:mb-4">
              <div className={`p-2 md:p-3 rounded-xl ${isDarkMode ? 'bg-blue-900/50 text-blue-400' : 'bg-blue-50 text-blue-600'}`}>
                <Target size={24} />
              </div>
              <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                isDarkMode ? 'text-blue-300 bg-blue-900/50' : 'text-blue-700 bg-blue-100'
              }`}>
                {goalsProgress}%
              </span>
            </div>
            <h3 className={`text-sm font-semibold mb-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Goals Progress</h3>
            <div className="flex items-baseline gap-2">
              <span className={`text-2xl md:text-4xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>{completedGoals}</span>
              <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>of {goals.length} goals</span>
            </div>
            <div className={`mt-3 w-full rounded-full h-2 ${isDarkMode ? 'bg-slate-700' : 'bg-slate-100'}`}>
              <div 
                className={`h-2 rounded-full transition-all ${isDarkMode ? 'bg-blue-500' : 'bg-blue-600'}`}
                style={{ width: `${goalsProgress}%` }}
              ></div>
            </div>
          </div>

          {/* Meeting Your Goals Card */}
          <div className={`rounded-2xl border shadow-sm p-4 md:p-6 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
            <div className="flex items-center justify-between mb-2 md:mb-4">
              <div className={`p-2 md:p-3 rounded-xl ${
                meetingGoals === 'exceeding' 
                  ? isDarkMode ? 'bg-primary-900/50 text-primary-400' : 'bg-primary-50 text-primary-600'
                  : meetingGoals === 'on-track'
                    ? isDarkMode ? 'bg-amber-900/50 text-amber-400' : 'bg-amber-50 text-amber-600'
                    : isDarkMode ? 'bg-rose-900/50 text-rose-400' : 'bg-rose-50 text-rose-600'
              }`}>
                <TrendingUp size={24} />
              </div>
              <span className={`whitespace-nowrap text-[10px] sm:text-xs font-semibold px-1.5 sm:px-2 py-1 rounded-full ${
                meetingGoals === 'exceeding'
                  ? isDarkMode ? 'text-primary-300 bg-primary-900/50' : 'text-primary-700 bg-primary-100'
                  : meetingGoals === 'on-track'
                    ? isDarkMode ? 'text-amber-300 bg-amber-900/50' : 'text-amber-700 bg-amber-100'
                    : isDarkMode ? 'text-rose-300 bg-rose-900/50' : 'text-rose-700 bg-rose-100'
              }`}>
                {meetingGoals === 'exceeding' ? 'Exceeding' :
                 meetingGoals === 'on-track' ? 'On Track' :
                 'Needs Improvement'}
              </span>
            </div>
            <h3 className={`text-sm font-semibold mb-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Meeting Your Goals</h3>
            <div className="flex items-baseline gap-2">
              <span className={`text-2xl md:text-4xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>{completionRate}%</span>
              <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>completion rate</span>
            </div>
            <p className={`text-xs mt-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              {meetingGoals === 'exceeding' ? 'You\'re doing great! Keep up the momentum.' :
               meetingGoals === 'on-track' ? 'You\'re making steady progress. Keep going!' :
               'Focus on completing more tasks to improve your progress.'}
            </p>
          </div>

          {/* Total Tasks Card */}
          <div className={`rounded-2xl border shadow-sm p-4 md:p-6 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
            <div className="flex items-center justify-between mb-2 md:mb-4">
              <div className={`p-2 md:p-3 rounded-xl ${isDarkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-50 text-slate-600'}`}>
                <BarChart3 size={24} />
              </div>
            </div>
            <h3 className={`text-sm font-semibold mb-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Total Tasks</h3>
            <div className="flex items-baseline gap-2">
              <span className={`text-2xl md:text-4xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>{totalTasks}</span>
              <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>active tasks</span>
            </div>
            {totalTasks > 0 ? (
              <p className={`text-xs mt-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                {completedTasks} completed, {totalTasks - completedTasks} remaining
              </p>
            ) : (
              <Link
                to="/actionitems"
                className={`mt-2 inline-flex items-center gap-1 text-xs font-semibold ${isDarkMode ? 'text-primary-400 hover:text-primary-300' : 'text-primary-600 hover:text-primary-700'}`}
              >
                Add a task
                <ArrowRight size={14} />
              </Link>
            )}
          </div>

          {/* Top Focus Area Card */}
          <div className={`rounded-2xl border shadow-sm p-4 md:p-6 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
            <div className="flex items-center justify-between mb-2 md:mb-4">
              <div className={`p-2 md:p-3 rounded-xl ${isDarkMode ? 'bg-purple-900/50 text-purple-400' : 'bg-purple-50 text-purple-600'}`}>
                <Award size={24} />
              </div>
            </div>
            <h3 className={`text-sm font-semibold mb-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Top Focus Area</h3>
            <div className="flex items-baseline gap-2">
              <span className={`text-2xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>{topFocusArea}</span>
            </div>
            <p className={`text-xs mt-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              Most active area of focus
            </p>
          </div>

          {/* Goals List Card */}
          <div className={`rounded-2xl border shadow-sm p-6 md:col-span-2 lg:col-span-1 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
            <div className="flex items-center justify-between mb-2 md:mb-4">
              <div className={`p-2 md:p-3 rounded-xl ${isDarkMode ? 'bg-indigo-900/50 text-indigo-400' : 'bg-indigo-50 text-indigo-600'}`}>
                <Calendar size={24} />
              </div>
            </div>
            <h3 className={`text-sm font-semibold mb-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Your Goals</h3>
            <ul className="space-y-3">
              {goals.map((goal) => (
                <li key={goal.id} className="flex items-center gap-3">
                  <div className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    goal.completed 
                      ? 'bg-primary-500 border-primary-500' 
                      : isDarkMode ? 'border-slate-600' : 'border-slate-300'
                  }`}>
                    {goal.completed && (
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                  <div className="flex-1">
                    <span className={`text-sm ${
                      goal.completed 
                        ? isDarkMode ? 'line-through text-slate-500' : 'line-through text-slate-400'
                        : isDarkMode ? 'text-slate-300' : 'text-slate-700'
                    }`}>
                      {goal.title}
                    </span>
                    <span className={`text-xs ml-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Due: {goal.due}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <section className="mb-8">
          <div className={`rounded-2xl border shadow-sm p-4 md:p-6 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
              <h2 className={`text-lg md:text-xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Weekly Progress by Focus Area</h2>
              {editingGoal ? (
                <div className="flex items-center gap-2">
                  <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Goal:</span>
                  <input
                    type="number"
                    min="1"
                    value={goalInput}
                    onChange={(e) => setGoalInput(e.target.value)}
                    className={`w-16 rounded-lg border px-2 py-1 text-sm ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-100' : 'bg-white border-slate-300 text-slate-900'}`}
                  />
                  <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>completed tasks per focus area</span>
                  <button
                    type="button"
                    disabled={savingGoal}
                    onClick={handleSaveWeeklyGoal}
                    className="text-sm font-semibold text-primary-600 hover:text-primary-700 disabled:opacity-60"
                  >
                    {savingGoal ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setEditingGoal(false); setGoalInput(String(weeklyGoal)); }}
                    className={`text-sm ${isDarkMode ? 'text-slate-400 hover:text-slate-300' : 'text-slate-500 hover:text-slate-700'}`}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setEditingGoal(true)}
                  className={`self-start text-left text-sm underline decoration-dotted ${isDarkMode ? 'text-slate-400 hover:text-slate-300' : 'text-slate-600 hover:text-slate-800'}`}
                >
                  Goal: {weeklyGoal} completed tasks per focus area (edit)
                </button>
              )}
            </div>

            {weeklyFocusProgress.length === 0 ? (
              <p className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>No weekly progress data yet.</p>
            ) : (
              <div className="space-y-4">
                {weeklyFocusProgress.map((row) => (
                  <div key={row.focusArea}>
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-sm font-semibold ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>{row.focusArea}</span>
                      <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                        {row.completed}/{row.weeklyGoal} completed ({row.progressPct}%)
                      </span>
                    </div>
                    <div className={`w-full rounded-full h-2.5 ${isDarkMode ? 'bg-slate-700' : 'bg-slate-100'}`}>
                      <div
                        className={`h-2.5 rounded-full ${isDarkMode ? 'bg-primary-500' : 'bg-primary-600'}`}
                        style={{ width: `${row.progressPct}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Additional Stats Section */}
        <section className="bg-primary-900 text-primary-50 rounded-2xl px-4 py-4 md:px-6 md:py-6">
          <h2 className="text-lg md:text-xl font-bold mb-4">Overall Performance</h2>
          <div className="grid grid-cols-2 gap-3 md:gap-4 lg:grid-cols-4">
            <div className="bg-primary-800/40 rounded-xl px-4 py-5">
              <div className="text-xs uppercase tracking-wide opacity-80">Tasks This Year</div>
              <div className="mt-2 text-2xl font-bold">{tasksCompletedThisYear}</div>
            </div>
            <div className="bg-primary-800/40 rounded-xl px-4 py-5">
              <div className="text-xs uppercase tracking-wide opacity-80">Completion Rate</div>
              <div className="mt-2 text-2xl font-bold">{completionRate}%</div>
            </div>
            <div className="bg-primary-800/40 rounded-xl px-4 py-5">
              <div className="text-xs uppercase tracking-wide opacity-80">Goals Completed</div>
              <div className="mt-2 text-2xl font-bold">{completedGoals}/{goals.length}</div>
            </div>
            <div className="bg-primary-800/40 rounded-xl px-4 py-5">
              <div className="text-xs uppercase tracking-wide opacity-80">Status</div>
              <div className="mt-2 text-lg font-bold capitalize">
                {meetingGoals === 'exceeding' ? '🌟 Exceeding' :
                 meetingGoals === 'on-track' ? '✓ On Track' :
                 '📈 Improving'}
              </div>
            </div>
          </div>
        </section>

        <section className="bg-primary-900 text-primary-50 rounded-2xl px-4 py-4 md:px-6 md:py-6 mt-6">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
            <h2 className="text-xl font-bold">Quarterly Task Plan</h2>
            <select
              value={quarterStatusFilter}
              onChange={(e) => setQuarterStatusFilter(e.target.value)}
              className="rounded-lg bg-primary-800/60 border border-primary-700 text-primary-50 text-sm px-3 py-1.5"
            >
              <option value="all">All tasks</option>
              <option value="unfinished">Unfinished</option>
              <option value="completed">Completed</option>
            </select>
          </div>
          <p className="text-sm opacity-90 mb-4">
            Current quarter: {currentQuarter?.QuarterName || orderedQuarterCards[0]?.quarter || 'N/A'}
          </p>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {orderedQuarterCards.map((card) => {
              const isCurrent = String(card.quarterId) === String(currentQuarter?.QuarterID);
              const visibleTasks = getFilteredQuarterTasks(card);
              return (
                <div key={card.quarter} className={`bg-primary-800/40 rounded-xl px-4 py-5 relative ${isCurrent ? 'ring-2 ring-primary-400' : ''}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-sm uppercase tracking-wide flex items-center gap-2">
                      {card.quarter}
                      {isCurrent && <span className="text-[10px] font-semibold bg-primary-500 text-primary-950 rounded-full px-2 py-0.5 normal-case">Current</span>}
                    </div>
                    <button
                      type="button"
                      className="h-8 w-8 rounded-full bg-primary-700/60 hover:bg-primary-700 grid place-items-center"
                      onClick={() => openQuarterModal(card)}
                      title={`Add task to ${card.quarter}`}
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                  <div className="mt-2 text-sm space-y-2">
                    {visibleTasks.length === 0 && (
                      <div className="opacity-80">
                        {card.tasks.length === 0 ? 'No tasks yet.' : 'No tasks match this filter.'}
                      </div>
                    )}
                    {visibleTasks.map((task) => (
                      <div key={task.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-lg bg-primary-950/40 px-3 py-2.5 sm:rounded-none sm:bg-transparent sm:px-0 sm:py-0">
                        <div className="flex items-start sm:items-center gap-2 min-w-0">
                          {task.status === 'Completed' ? (
                            <CheckCircle2 size={16} className="shrink-0 mt-0.5 sm:mt-0 text-primary-400" />
                          ) : (
                            <Circle size={16} className="shrink-0 mt-0.5 sm:mt-0 text-primary-100/40" />
                          )}
                          <span className={`break-words sm:truncate ${task.status === 'Completed' ? 'line-through opacity-70' : ''}`}>{task.title}</span>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0 self-end sm:self-auto">
                          <select
                            value={task.status}
                            disabled={updatingQuarterTaskId === String(task.id)}
                            onChange={(e) => handleQuarterTaskStatusChange(task, e.target.value)}
                            className={`rounded-lg border text-xs px-2 py-1 disabled:opacity-60 ${
                              task.status === 'Completed'
                                ? 'bg-primary-500/20 border-primary-400 text-primary-200'
                                : task.status === 'In Progress'
                                  ? 'bg-amber-500/15 border-amber-400/70 text-amber-200'
                                  : 'bg-primary-900/60 border-primary-700 text-primary-100/80'
                            }`}
                          >
                            <option value="Not Started">Not Started</option>
                            <option value="In Progress">In Progress</option>
                            <option value="Completed">Completed</option>
                          </select>
                          <button
                            type="button"
                            title="Remove from quarter"
                            disabled={removingQuarterTaskId === String(task.quarterTaskId)}
                            onClick={() => removeQuarterTask(task)}
                            className="h-7 w-7 rounded-full hover:bg-primary-900/60 grid place-items-center disabled:opacity-60"
                          >
                            <Trash2 size={14} className="text-primary-100/70" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {quarterModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[1000] flex items-center justify-center p-4">
            <div className={`w-full max-w-lg rounded-xl border ${isDarkMode ? 'bg-slate-800 border-slate-700 text-slate-100' : 'bg-white border-slate-200 text-slate-900'}`}>
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200/20">
                <h3 className="font-semibold">Add Task to {quarterModal.quarter}</h3>
                <button
                  type="button"
                  className={`h-8 w-8 rounded-full grid place-items-center ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-100'}`}
                  onClick={closeQuarterModal}
                >
                  <X size={16} />
                </button>
              </div>
              <div className="p-4 max-h-[420px] overflow-y-auto space-y-2">
                <button
                  type="button"
                  onClick={() => openCreateTaskForQuarter(quarterModal)}
                  className={`w-full text-left px-4 py-3 rounded-lg border border-dashed transition-colors font-semibold ${isDarkMode ? 'border-primary-600 text-primary-400 hover:bg-slate-700' : 'border-primary-400 text-primary-700 hover:bg-primary-50'}`}
                >
                  + Write a new task for this quarter
                </button>
                {availableModalTasks.length === 0 && (
                  <div className={`text-sm rounded-lg px-3 py-2 ${isDarkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>
                    No unassigned tasks due within this quarter's dates. Tasks only show up here if their due date falls inside {quarterModal.quarter}.
                  </div>
                )}
                {availableModalTasks.map((task) => (
                  <button
                    key={task.id}
                    type="button"
                    disabled={savingTaskId === String(task.id)}
                    onClick={() => assignTaskToQuarter(task)}
                    className={`w-full text-left px-4 py-3 rounded-lg border transition-colors disabled:opacity-60 flex items-center justify-between gap-2 ${isDarkMode ? 'border-slate-600 hover:bg-slate-700' : 'border-slate-200 hover:bg-primary-50'}`}
                  >
                    <span className="truncate">{task.title}</span>
                    <span className={`text-xs flex-shrink-0 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      {new Date(task.dueDate).toLocaleDateString()}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        <AddTaskModal
          isOpen={Boolean(createTaskForQuarter)}
          onClose={closeCreateTaskForQuarter}
          onTaskCreated={handleQuarterTaskCreated}
        />
      </div>
    </div>

    {/* Survey Modal */}
    {pendingSurvey && (
      <SurveyModal
        surveyData={pendingSurvey}
        onComplete={completeSurvey}
        onDismiss={() => dismissSurvey(pendingSurvey.SurveyID)}
        dismissible={true}
      />
    )}
    </>
  );
}
